import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ThemeToggle } from '../../../components/theme/ThemeToggle';
import { useAuthStore } from '../../../stores/auth.store';
import { trackUserEvent } from '../../../utils/reddit-pixel';

const navLinks: { label: string; href?: string; to?: string }[] = [
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Features', href: '#features' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Blog', to: '/learn' },
  { label: 'Mediation', to: '/conflict-resolution' },
];

/**
 * The two looks of this navbar. `legacy` is the one /learn, the articles and
 * /conflict-resolution render (out of the landing redesign's scope, so their
 * classes stay exactly as they were); `ee` is the landing's (redesign PR10,
 * design-system/pages/landing.css). Same markup, links and handlers in both.
 */
const LOOKS = {
  legacy: {
    nav: 'fixed top-0 left-0 right-0 z-(--ee-z-chrome) bg-(--ee-surface)/80 backdrop-blur-md border-b border-base-200',
    bar: 'max-w-6xl mx-auto px-4 h-16 flex items-center justify-between',
    brand: 'flex items-center gap-2',
    logo: 'h-10 w-10 rounded-xl',
    word: 'font-bold text-xl hidden sm:block',
    links: 'hidden md:flex items-center gap-6',
    link: 'text-sm font-medium text-base-content/60 hover:text-base-content transition-colors',
    actions: 'flex items-center gap-3',
    goApp:
      'px-5 py-2 bg-primary hover:bg-primary/90 text-(--ee-on-accent) rounded-lg font-medium transition-colors',
    login:
      'px-4 py-2 text-base-content/80 hover:text-base-content transition-colors font-medium hidden sm:block',
    preview:
      'px-4 py-2 border border-primary/40 text-primary hover:bg-primary/5 rounded-lg font-medium transition-colors hidden sm:block',
    join: 'px-5 py-2 bg-primary hover:bg-primary/90 text-(--ee-on-accent) rounded-lg font-medium transition-colors',
    menuBtn: 'md:hidden p-2',
    menuIcon: 'w-6 h-6',
    menu: 'md:hidden bg-(--ee-surface) border-t border-base-200 px-4 py-4 space-y-1',
    menuLink:
      'block py-2 text-base-content/70 hover:text-base-content transition-colors font-medium',
    menuGoApp: 'block py-2 text-primary hover:text-primary/80 transition-colors font-medium',
    menuPreview:
      'block w-full text-left py-2 text-primary hover:text-primary/80 transition-colors font-medium',
    theme: undefined,
    // The legacy bar has room for the toggle at every width (no wordmark on
    // a phone), so its menu carries none.
    menuTheme: 'hidden',
  },
  ee: {
    nav: 'ee-landing-nav',
    bar: 'ee-landing-nav__bar',
    brand: 'ee-landing-nav__brand',
    logo: 'ee-landing-nav__logo',
    word: 'ee-landing-nav__word',
    links: 'ee-landing-nav__links',
    link: 'ee-landing-nav__link',
    actions: 'ee-landing-nav__actions',
    goApp: 'ee-button ee-button--primary ee-button--sm',
    login: 'ee-landing-nav__login',
    preview: 'ee-button ee-button--secondary ee-button--sm ee-landing-nav__wide',
    join: 'ee-button ee-button--primary ee-button--sm',
    menuBtn: 'ee-landing-nav__menu-btn',
    menuIcon: 'ee-landing-nav__menu-icon',
    menu: 'ee-landing-nav__menu',
    menuLink: 'ee-landing-nav__menu-item',
    menuGoApp: 'ee-landing-nav__menu-item ee-landing-nav__menu-item--accent',
    menuPreview: 'ee-landing-nav__menu-item ee-landing-nav__menu-item--accent',
    // Under 480px the bar has no room beside the wordmark: the toggle moves
    // into the menu panel (landing.css).
    theme: 'ee-landing-nav__theme',
    menuTheme: 'ee-landing-nav__menu-theme',
  },
} as const;

export type LandingNavbarLook = keyof typeof LOOKS;

export default function LandingNavbar({ look = 'legacy' }: { look?: LandingNavbarLook }) {
  const c = LOOKS[look];
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setMobileMenuOpen(false);
      menuButtonRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      const firstLink = menuRef.current?.querySelector('a');
      firstLink?.focus();
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [mobileMenuOpen, handleEscape]);

  const handleMenuKeyDown = (e: React.KeyboardEvent) => {
    if (!menuRef.current) return;
    const focusable = menuRef.current.querySelectorAll<HTMLElement>('a, button');
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.key === 'Tab') {
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.startsWith('#')) {
      setMobileMenuOpen(false);
      return;
    }
    e.preventDefault();
    setMobileMenuOpen(false);
    const sectionId = href.slice(1);
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Navigate to landing page with scrollTo param (works from any page)
      void navigate(`/?scrollTo=${sectionId}`);
    }
  };

  return (
    // The starting-soon card sits just below this bar (components/circles/belowHeader.ts).
    <nav className={c.nav} data-notice-anchor="">
      <div className={c.bar}>
        {/* Logo */}
        <Link to="/" className={c.brand} aria-label="EmpathEase Home">
          <img src="/logo.webp" alt="EmpathEase" className={c.logo} />
          <span className={c.word}>EmpathEase</span>
        </Link>

        {/* Desktop nav links */}
        <div className={c.links}>
          {navLinks.map((link) =>
            link.to ? (
              <Link key={link.to} to={link.to} className={c.link}>
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleAnchorClick(e, link.href!)}
                className={c.link}
              >
                {link.label}
              </a>
            ),
          )}
        </div>

        {/* Actions */}
        <div className={c.actions}>
          {isAuthenticated ? (
            <Link to="/app" className={c.goApp}>
              Go to App
            </Link>
          ) : (
            <>
              <Link to="/login" className={c.login}>
                Login
              </Link>
              {/* Guest tour: walk the real app (intro course open, rest visible but
                  locked) with no account. See WS3 guest tour. */}
              <button
                onClick={() => {
                  trackUserEvent('Custom', { customEventName: 'ClickPreview' });
                  useAuthStore.getState().startGuest();
                  void navigate('/app');
                }}
                className={c.preview}
              >
                Preview
              </button>
              <Link
                to="/register"
                onClick={() => trackUserEvent('Custom', { customEventName: 'ClickJoin' })}
                className={c.join}
              >
                Join
              </Link>
            </>
          )}

          {/* Sun/moon (dark mode D9): absent until dark is available (D5) */}
          <ThemeToggle className={c.theme} testId="theme-toggle-header" />

          {/* Mobile menu button */}
          <button
            ref={menuButtonRef}
            className={c.menuBtn}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
            aria-controls={mobileMenuOpen ? 'landing-mobile-menu' : undefined}
          >
            <svg
              className={c.menuIcon}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              {mobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu: a disclosure of navigation links, deliberately NOT
          role="menu" (that promises menuitem children and arrow keys; the
          links are plain tab stops, b-016). A labelled group, so it keeps its
          name; Tab wraps inside it while it is open (the key handler hears
          the links' keydowns as they bubble). */}
      {mobileMenuOpen ? (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Tab wrap, heard from the links
        <div
          ref={menuRef}
          id="landing-mobile-menu"
          role="group"
          aria-label="Mobile navigation"
          onKeyDown={handleMenuKeyDown}
          className={c.menu}
        >
          {navLinks.map((link) =>
            link.to ? (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className={c.menuLink}
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleAnchorClick(e, link.href!)}
                className={c.menuLink}
              >
                {link.label}
              </a>
            ),
          )}
          {isAuthenticated ? (
            <Link to="/app" className={c.menuGoApp} onClick={() => setMobileMenuOpen(false)}>
              Go to App
            </Link>
          ) : (
            <>
              <Link to="/login" className={c.menuLink} onClick={() => setMobileMenuOpen(false)}>
                Login
              </Link>
              <button
                className={c.menuPreview}
                onClick={() => {
                  trackUserEvent('Custom', { customEventName: 'ClickPreview' });
                  setMobileMenuOpen(false);
                  useAuthStore.getState().startGuest();
                  void navigate('/app');
                }}
              >
                Preview the app
              </button>
            </>
          )}
          <ThemeToggle className={c.menuTheme} testId="theme-toggle-menu" />
        </div>
      ) : null}
    </nav>
  );
}
